/**
 * Step 9 · Block Model Estimation.
 *
 * P0 fix (Feb 2026) — replaces the single-slider "X = Y, m" control with
 * a single-source-of-truth geometry object (parent + sub-block X/Y/Z,
 * origin, extents, rotation). Every visible input, the request payload
 * and the block-count preview read from the same object so the frontend
 * can never fall out of sync with the backend again. Also:
 *   · surfaces the real backend validation error (never "Request failed
 *     with status code 400" again)
 *   · pre-flight validation panel shows blocking issues before submit
 *   · Apply auto-detected geometry populates all six numeric fields
 *   · Displays block-count tier: Recommended · Large · Very Large · Rejected
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Play, Sparkle, LockKey, LockKeyOpen, Warning, CheckCircle, XCircle, Copy,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { buildBlockModel, getBlockModel, api } from "@/lib/api";
import { WizardCard, StatusRow, KV, LoadingBanner, Pill } from "@/pages/wizard/_shared";
import InfluencePanel from "@/pages/wizard/block_model/InfluencePanel";


// ── Constants (mirror backend) ───────────────────────────────────────
const TIER_RECOMMENDED = 1_000_000;
const TIER_LARGE       = 5_000_000;
const TIER_VERY_LARGE  = 20_000_000;


// ── Block-count tier helpers ─────────────────────────────────────────
function classifyTier(n) {
  if (!Number.isFinite(n) || n <= 0)   return { key: "unknown",     label: "Unknown",     tone: "slate" };
  if (n <= TIER_RECOMMENDED)           return { key: "recommended", label: "Recommended", tone: "green" };
  if (n <= TIER_LARGE)                 return { key: "large",       label: "Large",       tone: "amber" };
  if (n <= TIER_VERY_LARGE)            return { key: "very_large",  label: "Very Large",  tone: "amber" };
  return                                       { key: "rejected",   label: "Rejected",    tone: "red"   };
}


// ── Validation ───────────────────────────────────────────────────────
function validateGeometry(g) {
  const issues = [];
  const finite = (v) => Number.isFinite(v) && !Number.isNaN(v);
  ["x", "y", "z"].forEach((ax) => {
    if (!finite(g.parent[ax]) || g.parent[ax] <= 0)
      issues.push({ field: `parentBlock.${ax}`, msg: `Parent block ${ax.toUpperCase()} must be > 0.` });
    if (!finite(g.sub[ax]) || g.sub[ax] <= 0)
      issues.push({ field: `subBlock.${ax}`, msg: `Sub-block ${ax.toUpperCase()} must be > 0.` });
    if (finite(g.parent[ax]) && finite(g.sub[ax])) {
      if (g.sub[ax] > g.parent[ax])
        issues.push({ field: `subBlock.${ax}`, msg: `Sub-block ${ax.toUpperCase()} (${g.sub[ax]}) exceeds parent ${ax.toUpperCase()} (${g.parent[ax]}).` });
      else {
        const ratio = g.parent[ax] / g.sub[ax];
        if (Math.abs(ratio - Math.round(ratio)) > 1e-6)
          issues.push({ field: `subBlock.${ax}`, msg: `Sub-block ${ax.toUpperCase()} (${g.sub[ax]}) must divide parent ${ax.toUpperCase()} (${g.parent[ax]}) exactly.` });
      }
    }
    if (!finite(g.origin[ax]))
      issues.push({ field: `origin.${ax}`, msg: `Origin ${ax.toUpperCase()} must be a finite number.` });
    if (!finite(g.extents[ax]) || g.extents[ax] <= 0)
      issues.push({ field: `extents.${ax}`, msg: `Extent ${ax.toUpperCase()} must be > 0.` });
  });
  return issues;
}


function computeBlockCount(g) {
  const nx = Math.ceil(g.extents.x / g.parent.x);
  const ny = Math.ceil(g.extents.y / g.parent.y);
  const nz = Math.ceil(g.extents.z / g.parent.z);
  const nParent = Number.isFinite(nx * ny * nz) && nx > 0 && ny > 0 && nz > 0 ? nx * ny * nz : 0;
  return { nx, ny, nz, nParent };
}


// ── Numeric input primitive ──────────────────────────────────────────
function NumField({ label, value, onChange, step = 1, testId, disabled = false }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold text-slate-600">{label}</Label>
      <Input type="number" step={step} value={value ?? ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
              className="h-8 text-[12px] font-mono"
              data-testid={testId} />
    </div>
  );
}


// ── Structured error panel (from backend detail) ─────────────────────
function BackendErrorPanel({ err, onRetry }) {
  if (!err) return null;
  const detail = err.response?.data?.detail;
  const message = (typeof detail === "object" ? detail?.message : detail) || err.message || "Unknown error";
  const code = typeof detail === "object" ? detail?.code : undefined;
  const errors = typeof detail === "object" ? (detail?.errors || []) : [];
  const requestId = typeof detail === "object" ? detail?.requestId : undefined;
  const copyId = () => { if (requestId) navigator.clipboard.writeText(requestId); toast.success("Request ID copied"); };
  return (
    <div className="rounded border border-red-200 bg-red-50 p-3 space-y-2"
          data-testid="w-bm-error-panel">
      <div className="flex items-start gap-2">
        <XCircle size={16} weight="fill" className="text-red-600 shrink-0 mt-[1px]" />
        <div className="flex-1">
          <div className="text-[13px] font-bold text-red-900" data-testid="w-bm-error-title">
            Block Model Build Failed
          </div>
          <div className="text-[12px] text-red-800 mt-0.5" data-testid="w-bm-error-message">
            {message}
          </div>
          {code && <div className="text-[10px] font-mono text-red-700/80 mt-1">Code: {code}</div>}
        </div>
      </div>
      {errors.length > 0 && (
        <ul className="text-[11px] text-red-900 pl-6 list-disc space-y-0.5"
            data-testid="w-bm-error-list">
          {errors.map((e, i) => (
            <li key={i}><b className="font-mono">{e.field}</b> — {e.message}</li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 pt-1">
        {requestId && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={copyId} data-testid="w-bm-error-copy-request">
            <Copy size={11} className="mr-1" /> {requestId}
          </Button>
        )}
        {onRetry && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={onRetry} data-testid="w-bm-error-retry">
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}




export default function BlockModelWizardStep({ projectId, onDataChanged }) {
  const [method, setMethod] = useState("idw");
  const [lockXY, setLockXY] = useState(true);
  // Single source of truth for every visible field + request payload.
  const [geometry, setGeometry] = useState({
    origin:  { x: 0, y: 0, z: 0 },
    extents: { x: 500, y: 500, z: 100 },
    rotation:{ azimuth: 0, plunge: 0, dip: 0 },
    parent:  { x: 20, y: 20, z: 5 },
    sub:     { x: 10, y: 10, z: 2.5 },
    source: "manual",
    version: `manual-${Date.now()}`,
  });
  // Sprint P31 · Drillhole buffer / influence zone options.
  // Every one of these is optional and defaults to a safe value.
  const [influence, setInfluence] = useState({
    // Buffer type: circular (isotropic) or ellipsoid (anisotropic).
    kind: "circular",              // circular | ellipsoid
    search_radius: 100,            // isotropic radius (m)
    search_rx: 100, search_ry: 100, search_rz: 25,   // ellipsoid axes
    // Ellipsoid orientation (mining convention · azimuth/plunge/dip).
    azimuth: 0, plunge: 0, dip: 0,
    // Per-collar exclusion buffer — samples closer than this ignored.
    borehole_influence_radius: 0,
    // Domain / lithology constraint.
    domain_aware: false,
    hard_boundary: true,
    by_lithology: false,
    // Sample density / max samples per estimate.
    max_samples: 12,
    min_samples: 1,
  });
  const [progress, setProgress] = useState(null);
  const [lastError, setLastError] = useState(null);
  const qc = useQueryClient();

  // ── Server-provided auto-detected geometry ────────────────────────
  const geoQ = useQuery({
    queryKey: ["wizard-bm-geometry", projectId],
    queryFn: () => api.get(`/projects/${projectId}/block-model/geometry-suggestion`)
                      .then((r) => r.data),
    enabled: !!projectId, staleTime: 60_000, retry: false,
  });
  const bmQ = useQuery({
    queryKey: ["wizard-bm", projectId],
    queryFn: () => getBlockModel(projectId),
    enabled: !!projectId, retry: false, staleTime: 5_000,
  });

  // Pull the project's canonical influence radius (set at project-setup
  // stage, P31) so we auto-populate the ellipsoid axes if the user hasn't
  // touched them yet.
  const projectQ = useQuery({
    queryKey: ["wizard-bm-project", projectId],
    queryFn: () => api.get(`/projects/${projectId}`).then((r) => r.data),
    enabled: !!projectId, staleTime: 60_000,
  });
  useEffect(() => {
    const rad = Number(projectQ.data?.influence_radius_m);
    if (!Number.isFinite(rad) || rad <= 0) return;
    setInfluence((inf) => (inf._userTouched ? inf : {
      ...inf,
      search_radius: rad,
      search_rx:     rad,
      search_ry:     rad,
      search_rz:     Math.max(5, rad * 0.4),
    }));
  }, [projectQ.data]);

  // Apply auto-detected → populates every field from geoQ.data.
  const applyGeoSuggestion = () => {
    const g = geoQ.data;
    if (!g?.ok || !g.parent_block_size) return;
    setGeometry({
      origin:  { x: g.origin.x,  y: g.origin.y,  z: g.origin.z  },
      extents: { x: g.extents.x, y: g.extents.y, z: g.extents.z },
      rotation:{ azimuth: g.rotation.azimuth, plunge: g.rotation.plunge, dip: g.rotation.dip },
      parent:  { x: g.parent_block_size.x, y: g.parent_block_size.y, z: g.parent_block_size.z },
      sub:     { x: g.sub_block_size.x,    y: g.sub_block_size.y,    z: g.sub_block_size.z    },
      source: "auto_detected",
      version: `auto-${Date.now()}`,
    });
    toast.success(`Auto-detected geometry applied — parent ${g.parent_block_size.x}×${g.parent_block_size.y}×${g.parent_block_size.z} m (${g.counts?.estimated_parent_blocks?.toLocaleString?.() || g.estimated_block_count?.toLocaleString?.() || ""} blocks)`);
  };

  // ── Live-computed block count + tier + validation ────────────────
  const counts = useMemo(() => computeBlockCount(geometry), [geometry]);
  const tier   = useMemo(() => classifyTier(counts.nParent), [counts.nParent]);
  const issues = useMemo(() => validateGeometry(geometry),   [geometry]);
  const canBuild = issues.length === 0 && tier.key !== "rejected" && counts.nParent > 0;

  const setParent = (axis) => (v) => setGeometry((g) => {
    const next = { ...g.parent, [axis]: v };
    if (lockXY && axis === "x") { next.y = v; }
    if (lockXY && axis === "y") { next.x = v; }
    return { ...g, parent: next, source: "manual" };
  });
  const setSub = (axis) => (v) => setGeometry((g) => {
    const next = { ...g.sub, [axis]: v };
    if (lockXY && axis === "x") { next.y = v; }
    if (lockXY && axis === "y") { next.x = v; }
    return { ...g, sub: next, source: "manual" };
  });
  const setOrigin  = (axis) => (v) => setGeometry((g) => ({ ...g, origin:  { ...g.origin,  [axis]: v }, source: "manual" }));
  const setExtents = (axis) => (v) => setGeometry((g) => ({ ...g, extents: { ...g.extents, [axis]: v }, source: "manual" }));

  // ── Build mutation — explicit bx/by/bz always (no more block_size[]) ──
  const buildMut = useMutation({
    mutationFn: () => {
      const isEllipsoid = influence.kind === "ellipsoid";
      const payload = {
        method,
        // Normalized primitives — no NaN, no strings, no formatted text.
        bx: Number(geometry.parent.x),
        by: Number(geometry.parent.y),
        bz: Number(geometry.parent.z),
        sub_bx: Number(geometry.sub.x),
        sub_by: Number(geometry.sub.y),
        sub_bz: Number(geometry.sub.z),
        origin:  [Number(geometry.origin.x), Number(geometry.origin.y), Number(geometry.origin.z)],
        extents: [Number(geometry.extents.x), Number(geometry.extents.y), Number(geometry.extents.z)],
        // Geometry rotation (block model grid orientation)
        azimuth: Number(geometry.rotation.azimuth),
        plunge:  Number(geometry.rotation.plunge),
        dip:     Number(geometry.rotation.dip),
        // Sprint P31 · Drillhole influence / buffer parameters
        search_radius: Number(influence.search_radius),
        ...(isEllipsoid ? {
          search_rx: Number(influence.search_rx),
          search_ry: Number(influence.search_ry),
          search_rz: Number(influence.search_rz),
          // Ellipsoid orientation overrides grid orientation for search
          azimuth: Number(influence.azimuth),
          plunge:  Number(influence.plunge),
          dip:     Number(influence.dip),
        } : {}),
        borehole_influence_radius: Number(influence.borehole_influence_radius) || 0,
        domain_aware:  !!influence.domain_aware,
        hard_boundary: !!influence.hard_boundary,
        by_lithology:  !!influence.by_lithology,
        max_samples: Number(influence.max_samples) || 12,
      };
      setLastError(null);
      return buildBlockModel(projectId, payload, { onProgress: setProgress });
    },
    onSuccess: () => {
      toast.success("Block model built");
      setProgress(null); setLastError(null);
      qc.invalidateQueries({ queryKey: ["wizard-bm", projectId] });
      bmQ.refetch(); onDataChanged?.();
    },
    onError: (e) => {
      setProgress(null);
      setLastError(e);
      // Show the *real* backend message in the toast (fallback to axios message).
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" ? detail?.message : detail) || e?.message || "Estimation failed";
      toast.error(String(msg));
    },
  });

  const bm = bmQ.data;
  const s = bm?.summary || {};
  const g = geoQ.data;

  return (
    <div className="max-w-5xl space-y-4">
      {bm
        ? <StatusRow tone="ok">
            Block model exists — <b>{(s.ore_blocks || 0).toLocaleString()} ore blocks</b>
            {" "}@ mean <b>{s.avg_ore_grade?.toFixed?.(3)}</b>{" "}
            <b>{s.grade_symbol}</b>. Rebuild with different settings below if needed.
          </StatusRow>
        : <StatusRow tone="warn">
            No block model built yet. Set the geometry, click Apply auto-detected, or edit
            values manually and press <b>Build</b>.
          </StatusRow>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ── Estimation parameters (single-source-of-truth) ── */}
        <WizardCard title="Estimation parameters" testId="w-bm-params"
                     right={
                       g?.ok && (
                         <Button size="sm" variant="outline"
                                   onClick={applyGeoSuggestion}
                                   data-testid="w-bm-apply-geometry">
                           <Sparkle size={11} weight="fill" className="mr-1" />
                           Apply auto-detected geometry
                         </Button>
                       )
                     }>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="w-bm-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="idw">Inverse Distance (IDW)</SelectItem>
                <SelectItem value="ok">Ordinary Kriging (OK)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Parent block X/Y/Z */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[11px] font-semibold text-slate-600">
                Parent block size (m)
              </Label>
              <label className="inline-flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer">
                <Checkbox checked={lockXY} onCheckedChange={(v) => setLockXY(!!v)}
                            data-testid="w-bm-lock-xy" />
                {lockXY ? <LockKey size={11} /> : <LockKeyOpen size={11} />}
                Lock X = Y
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Parent X" value={geometry.parent.x} onChange={setParent("x")} step="0.1" testId="w-bm-parent-x" />
              <NumField label="Parent Y" value={geometry.parent.y} onChange={setParent("y")} step="0.1" testId="w-bm-parent-y" disabled={lockXY} />
              <NumField label="Parent Z" value={geometry.parent.z} onChange={setParent("z")} step="0.1" testId="w-bm-parent-z" />
            </div>
          </div>

          {/* Sub-block X/Y/Z */}
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Sub-block size (m)</Label>
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Sub X" value={geometry.sub.x} onChange={setSub("x")} step="0.05" testId="w-bm-sub-x" />
              <NumField label="Sub Y" value={geometry.sub.y} onChange={setSub("y")} step="0.05" testId="w-bm-sub-y" disabled={lockXY} />
              <NumField label="Sub Z" value={geometry.sub.z} onChange={setSub("z")} step="0.05" testId="w-bm-sub-z" />
            </div>
          </div>

          {/* Origin */}
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Origin (m)</Label>
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Origin X" value={geometry.origin.x} onChange={setOrigin("x")} step="1" testId="w-bm-origin-x" />
              <NumField label="Origin Y" value={geometry.origin.y} onChange={setOrigin("y")} step="1" testId="w-bm-origin-y" />
              <NumField label="Origin Z" value={geometry.origin.z} onChange={setOrigin("z")} step="0.1" testId="w-bm-origin-z" />
            </div>
          </div>

          {/* Extents */}
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Extent (m)</Label>
            <div className="grid grid-cols-3 gap-2">
              <NumField label="Extent X" value={geometry.extents.x} onChange={setExtents("x")} step="1"   testId="w-bm-ext-x" />
              <NumField label="Extent Y" value={geometry.extents.y} onChange={setExtents("y")} step="1"   testId="w-bm-ext-y" />
              <NumField label="Extent Z" value={geometry.extents.z} onChange={setExtents("z")} step="0.1" testId="w-bm-ext-z" />
            </div>
          </div>

          {/* Block count preview + tier */}
          <div className="rounded border border-slate-200 bg-slate-50 p-2 text-[11px] font-mono">
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-600">Grid: {counts.nx} × {counts.ny} × {counts.nz}</span>
              <Pill tone={tier.tone} testId="w-bm-tier-pill">
                {tier.label}
              </Pill>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-slate-500">Theoretical parent blocks</span>
              <span className="ml-auto text-slate-900 font-bold" data-testid="w-bm-parent-count">
                {counts.nParent.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Pre-build blocking issues */}
          {(issues.length > 0 || tier.key === "rejected") && (
            <div className="rounded border border-red-200 bg-red-50 p-2 text-[11px]"
                  data-testid="w-bm-blocking">
              <div className="flex items-center gap-1 text-red-900 font-bold mb-1">
                <Warning size={12} weight="fill" />
                Blocking Issues
              </div>
              <ul className="pl-5 list-disc text-red-900 space-y-0.5">
                {tier.key === "rejected" && (
                  <li>Block count {counts.nParent.toLocaleString()} exceeds the platform limit of {TIER_VERY_LARGE.toLocaleString()}. Increase parent block size or reduce extent.</li>
                )}
                {issues.map((i, ix) => (
                  <li key={ix}><b className="font-mono">{i.field}</b> — {i.msg}</li>
                ))}
              </ul>
            </div>
          )}
          {tier.key === "very_large" && issues.length === 0 && (
            <StatusRow tone="warn">
              Very large model ({counts.nParent.toLocaleString()} blocks). Build will run as
              an async job — you can leave this page and it will keep running.
            </StatusRow>
          )}

          {/* Sprint P31 · Drillhole buffer / influence zone controls */}
          <InfluencePanel influence={influence} setInfluence={setInfluence}
                            projectRadius={projectQ.data?.influence_radius_m} />

          <Button size="sm" onClick={() => buildMut.mutate()}
                    disabled={buildMut.isPending || !canBuild}
                    className="bg-brand-blue hover:bg-brand-blue/90 text-white disabled:opacity-50"
                    data-testid="w-bm-run">
            <Play size={12} className="mr-1" />
            {buildMut.isPending ? "Building…" : "Build block model"}
          </Button>

          {progress && (
            <div className="rounded border border-brand-blue/30 bg-brand-blue/5 p-2">
              <div className="text-[11px] font-mono text-brand-blue mb-1">
                {progress.status?.toUpperCase()} — {progress.progress ?? 0}%
                {progress.message ? ` · ${progress.message}` : ""}
              </div>
              <div className="h-1.5 bg-brand-blue/20 rounded-full overflow-hidden">
                <div className="h-full bg-brand-blue transition-all duration-500"
                     style={{ width: `${progress.progress ?? 0}%` }} />
              </div>
            </div>
          )}
          {buildMut.isPending && !progress && (
            <LoadingBanner label="Kicking off estimator…" />
          )}
          <BackendErrorPanel err={lastError} onRetry={() => buildMut.mutate()} />
        </WizardCard>

        {/* ── Current block model summary ── */}
        <WizardCard title="Current block model" testId="w-bm-summary">
          {!bm
            ? <div className="text-[12px] text-slate-500">No model yet.</div>
            : (
              <>
                <KV k="Method"           v={s.method?.toUpperCase?.() || "—"} />
                <KV k="Total blocks"     v={s.total_blocks?.toLocaleString?.() ?? "—"} />
                <KV k="Ore blocks"       v={s.ore_blocks?.toLocaleString?.() ?? "—"} />
                <KV k="Waste blocks"     v={s.waste_blocks?.toLocaleString?.() ?? "—"} />
                <KV k="Cutoff"           v={`${s.cutoff_grade ?? "—"} ${s.grade_symbol ?? ""}`} />
                <KV k="Mean ore grade"   v={s.avg_ore_grade?.toFixed?.(3) ?? "—"} />
                <KV k="Contained metal"  v={`${s.contained_metal_t?.toFixed?.(2) ?? "—"} t`} />
                <KV k="Total ore tonnes" v={s.total_ore_tonnes?.toLocaleString?.() ?? "—"} />
              </>
            )}
        </WizardCard>
      </div>

      {/* ── Auto-detected geometry reference card ── */}
      {g?.ok && (
        <WizardCard title="Auto-detected block model geometry"
                     subtitle="Datamine / Surpac / Leapfrog Edge convention"
                     testId="w-bm-geometry-card"
                     right={
                       <Pill tone={classifyTier(g.counts?.estimated_parent_blocks ?? g.estimated_block_count).tone}
                              testId="w-bm-auto-tier">
                         {classifyTier(g.counts?.estimated_parent_blocks ?? g.estimated_block_count).label}
                       </Pill>
                     }>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV k="Origin (X, Y, Z)"
                v={`${g.origin.x} · ${g.origin.y} · ${g.origin.z}`} />
            <KV k="Extents (X, Y, Z)"
                v={`${g.extents.x} · ${g.extents.y} · ${g.extents.z}`} />
            <KV k="Parent block"
                v={`${g.parent_block_size.x} × ${g.parent_block_size.y} × ${g.parent_block_size.z} m`} />
            <KV k="Sub-block"
                v={`${g.sub_block_size.x} × ${g.sub_block_size.y} × ${g.sub_block_size.z} m`} />
            <KV k="Rotation"
                v={`Az ${g.rotation.azimuth}° · Dip ${g.rotation.dip}° · Plunge ${g.rotation.plunge}°`} />
            <KV k="Median hole spacing"
                v={`${g.median_hole_spacing_m} m`} />
            <KV k="Estimated parent blocks"
                v={(g.counts?.estimated_parent_blocks ?? g.estimated_block_count)?.toLocaleString?.()} />
            <KV k="Rotation source"
                v={g.rotation.source} />
          </div>
          {Array.isArray(g.warnings) && g.warnings.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900"
                  data-testid="w-bm-auto-warnings">
              <div className="font-bold mb-1 flex items-center gap-1">
                <Warning size={11} weight="fill" /> Auto-detect warnings
              </div>
              <ul className="pl-5 list-disc space-y-0.5">
                {g.warnings.map((w, i) => (<li key={i}>{w}</li>))}
              </ul>
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-2 font-mono">
            {Object.entries(g.rationale || {}).map(([k, v]) => (
              <span key={k} className="inline-block mr-3">• {k}: {v}</span>
            ))}
          </div>
        </WizardCard>
      )}
    </div>
  );
}
